import { generateDailyPuzzle, modeForDate } from '../../engine/generate';
import { fmtTime, t } from '../../i18n/runtime';
import { haptic } from '../../platform/bridge';
import { profile, recordSolve, spendCredit } from '../../state/profile';
import { postSolve } from '../../state/sync';
/**
 * G2 — Daily Challenge play.
 * Per UI-SPEC §3 G2 + GAME-SPEC §1.1.
 *
 * Chrome fades on first valid drag; timer + rarity + reset visible at rest.
 */
import { el, html, on } from '../html';
import { iconString } from '../icons';
import { type PlayController, mountPlay } from '../play-engine';
import type { RouteContext } from '../router';

function modeForToday(dateStr: string): string {
  return modeForDate(new Date(dateStr + 'T00:00:00Z'));
}

const RARITY_SYMBOL: Record<string, string> = {
  common: '●',
  uncommon: '◆',
  rare: '▲',
  epic: '★',
  legendary: '✦',
};

export default function (
  ctx: RouteContext,
): HTMLElement | { node: HTMLElement; cleanup: () => void } {
  const dateParam = ctx.params['date'] ?? '';
  const puzzle = generateDailyPuzzle(dateParam);

  // Spend a credit on play start (only for daily; tutorial / practice handled elsewhere).
  if (!spendCredit()) {
    // Defer: route to ad gate.
    queueMicrotask(() => ctx.navigate('/ad-gate'));
    return el(
      html`<section class="screen"><p style="padding:24px">${t('common.loading')}</p></section>`,
    );
  }

  let controller: PlayController | null = null;
  let timerHandle: ReturnType<typeof setInterval> | null = null;
  let chromeTimer: ReturnType<typeof setTimeout> | null = null;
  const startedAt = Date.now();

  const root = el(html`
    <section class="screen screen-play" data-chrome="full" data-rarity="${puzzle.rarity}" aria-label="${t('home.today_label')}">
      <header class="play-topbar" data-topbar>
        <button class="topbar-btn" aria-label="${t('common.close')}" data-exit>${iconString('close', 20)}</button>
        <span class="topbar-rarity rarity-${puzzle.rarity}" aria-label="${t(`rarity.${puzzle.rarity}`)}">${RARITY_SYMBOL[puzzle.rarity]}</span>
        <span class="topbar-timer" aria-label="${t('game.timer_label')}">
          <span class="icon" aria-hidden="true">⏱</span><span class="time" data-timer>0:00</span>
        </span>
      </header>
      <div class="play-canvas-wrap" data-canvas></div>
      <footer class="play-footer">
        <button class="ghost-btn" data-reset>${t('game.reset_path')}</button>
        <span class="progress">
          <span data-done>0</span> / <span data-total>${puzzle.edges.length}</span> ${t('game.edges')}
        </span>
      </footer>
    </section>
  `);

  const canvas = root.querySelector('[data-canvas]') as HTMLElement;
  const doneEl = root.querySelector('[data-done]') as HTMLElement;
  const timerEl = root.querySelector('[data-timer]') as HTMLElement;
  const reduceMotion =
    profile().get().a11y.reduceMotion === 'on' ||
    (profile().get().a11y.reduceMotion === 'auto' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches);

  controller = mountPlay(canvas, {
    puzzle,
    reduceMotion,
    onProgress: (done) => {
      doneEl.textContent = String(done);
      void haptic('light');
    },
    onWin: ({ timeMs, attempts }) => {
      const dur = Math.round(timeMs / 1000);
      const record = {
        date: dateParam,
        timeMs: dur * 1000,
        attempts,
        rarity: puzzle.rarity,
        mode: modeForToday(dateParam),
      };
      recordSolve(record);
      void haptic('success');
      void postSolve(record, puzzle.edges.length);
      if (timerHandle) clearInterval(timerHandle);
      setTimeout(() => ctx.navigate(`/solve/${dateParam}?t=${timeMs}&a=${attempts}`), 700);
    },
  });

  // Timer tick.
  timerHandle = setInterval(() => {
    timerEl.textContent = fmtTime((Date.now() - startedAt) / 1000);
  }, 250);

  // Chrome fade ladder.
  chromeTimer = setTimeout(() => root.setAttribute('data-chrome', 'faded'), 1500);
  canvas.addEventListener('pointerdown', () => {
    if (chromeTimer) clearTimeout(chromeTimer);
    root.setAttribute('data-chrome', 'full');
    chromeTimer = setTimeout(() => root.setAttribute('data-chrome', 'faded'), 1500);
  });

  on(root, 'click', '[data-exit]', () => {
    if (confirm(t('game.confirm_exit'))) ctx.navigate('/');
  });
  on(root, 'click', '[data-reset]', () => controller?.reset());

  return {
    node: root,
    cleanup: () => {
      if (timerHandle) clearInterval(timerHandle);
      if (chromeTimer) clearTimeout(chromeTimer);
      controller?.destroy();
    },
  };
}
